/**
 * Mobile: each swipe is either inline scroll or slide change (never both).
 * Decision is made once per gesture from scroll position + swipe direction.
 * At bottom + new swipe up -> next slide. At top + new swipe down -> prev slide.
 */

const SCROLL_EDGE_TOLERANCE = 2
const GESTURE_LOCK_THRESHOLD = 8

const NESTED_SCROLL_SKIP_SELECTOR = [
	'[data-mobile-days-scroll]',
	'a',
	'button',
	'input',
	'textarea',
	'select',
	'label',
	'[role="button"]',
	'.swiper-no-swiping',
].join(', ')

const getOverflowScrollParent = (root, target) => {
	let el = target

	while (el && el !== root) {
		if (el.scrollHeight > el.clientHeight + SCROLL_EDGE_TOLERANCE) {
			const { overflowY } = window.getComputedStyle(el)

			if (
				overflowY === 'auto' ||
				overflowY === 'scroll' ||
				overflowY === 'overlay'
			) {
				return el
			}
		}

		el = el.parentElement
	}

	if (root.scrollHeight > root.clientHeight + SCROLL_EDGE_TOLERANCE) {
		return root
	}

	return null
}

/**
 * Decide once per gesture: inline scroll or slide swipe.
 * @param {number} deltaY Finger movement since gesture start.
 *   Positive = down, negative = up.
 */
const shouldUseInnerScroll = (scrollEl, deltaY) => {
	const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight

	if (maxScroll <= SCROLL_EDGE_TOLERANCE) {
		return false
	}

	const scrollTop = scrollEl.scrollTop
	const atTop = scrollTop <= SCROLL_EDGE_TOLERANCE
	const atBottom = scrollTop >= maxScroll - SCROLL_EDGE_TOLERANCE
	const inMiddle =
		scrollTop > SCROLL_EDGE_TOLERANCE &&
		scrollTop < maxScroll - SCROLL_EDGE_TOLERANCE

	if (inMiddle) {
		return true
	}

	if (atTop && deltaY < 0) {
		return true
	}

	if (atBottom && deltaY > 0) {
		return true
	}

	return false
}

const resetSwiperGesture = (swiper) => {
	const data = swiper.touchEventsData
	if (!data) {
		return
	}

	data.isTouched = false
	data.isMoved = false
	data.startMoving = undefined
	data.isScrolling = undefined
}

const primeSwiperGesture = (swiper, pageX, pageY) => {
	const data = swiper.touchEventsData
	const touches = swiper.touches
	if (!data || !touches) {
		return
	}

	touches.startX = pageX
	touches.startY = pageY
	touches.currentX = pageX
	touches.currentY = pageY
	touches.previousX = pageX
	touches.previousY = pageY
	data.isTouched = true
	data.isMoved = false
	data.startMoving = undefined
	data.isScrolling = undefined
	data.touchStartTime = Date.now()
}

/**
 * @param {() => { allowTouchMove: boolean, params: { allowTouchMove: boolean }, touchEventsData?: object } | null | undefined} getSwiper
 * @param {() => HTMLElement | null} getActiveSlideInner
 */
export function initSwiperNestedScroll(getSwiper, getActiveSlideInner) {
	let touchStartY = 0
	let touchStartX = 0
	let activeScrollEl = null
	let defaultAllowTouchMove = true
	/** @type {null | 'pending' | 'inner' | 'slide'} */
	let gestureMode = null
	let activeTouchId = null

	const reset = () => {
		activeScrollEl = null
		gestureMode = null
		activeTouchId = null
		const swiper = getSwiper()

		if (swiper) {
			swiper.allowTouchMove = defaultAllowTouchMove
		}
	}

	const beginGesture = (target, pageX, pageY) => {
		const swiper = getSwiper()
		if (!swiper) {
			return
		}

		if (target.closest?.(NESTED_SCROLL_SKIP_SELECTOR)) {
			return
		}

		const slideInner = target.closest?.('.fullpage-slide__inner')
		const activeInner = getActiveSlideInner()

		if (!slideInner || slideInner !== activeInner) {
			return
		}

		activeScrollEl = getOverflowScrollParent(slideInner, target)

		if (!activeScrollEl) {
			return
		}

		defaultAllowTouchMove = swiper.params.allowTouchMove
		touchStartX = pageX
		touchStartY = pageY
		gestureMode = 'pending'
		swiper.allowTouchMove = false
		resetSwiperGesture(swiper)
	}

	const handleMove = (event, pageX, pageY) => {
		if (!activeScrollEl) {
			return
		}

		const swiper = getSwiper()
		if (!swiper) {
			return
		}

		const deltaX = pageX - touchStartX
		const deltaY = pageY - touchStartY

		if (gestureMode === 'pending') {
			if (
				Math.abs(deltaX) < GESTURE_LOCK_THRESHOLD &&
				Math.abs(deltaY) < GESTURE_LOCK_THRESHOLD
			) {
				swiper.allowTouchMove = false
				event.stopImmediatePropagation()
				return
			}

			gestureMode = shouldUseInnerScroll(activeScrollEl, deltaY)
				? 'inner'
				: 'slide'

			if (gestureMode === 'slide') {
				swiper.allowTouchMove = true
				primeSwiperGesture(swiper, pageX, pageY)
				return
			}
		}

		if (gestureMode === 'inner') {
			swiper.allowTouchMove = false
			resetSwiperGesture(swiper)
			event.stopImmediatePropagation()
			return
		}

		swiper.allowTouchMove = true
	}

	const onPointerDown = (event) => {
		if (event.pointerType === 'mouse' || activeTouchId !== null) {
			return
		}

		beginGesture(event.target, event.pageX, event.pageY)
	}

	const onPointerMove = (event) => {
		if (event.pointerType === 'mouse' || !activeScrollEl || activeTouchId !== null) {
			return
		}

		handleMove(event, event.pageX, event.pageY)
	}

	const onTouchStart = (event) => {
		if (!event.touches?.length || activeTouchId !== null) {
			return
		}

		activeTouchId = event.touches[0].identifier
		const touch = event.touches[0]
		beginGesture(event.target, touch.pageX, touch.pageY)
	}

	const onTouchMove = (event) => {
		if (!activeScrollEl || !event.touches?.length) {
			return
		}

		const touch = event.touches[0]
		if (touch.identifier !== activeTouchId) {
			return
		}

		handleMove(event, touch.pageX, touch.pageY)
	}

	const captureOpts = { capture: true, passive: true }

	document.addEventListener('pointerdown', onPointerDown, captureOpts)
	document.addEventListener('pointermove', onPointerMove, captureOpts)
	document.addEventListener('pointerup', reset, captureOpts)
	document.addEventListener('pointercancel', reset, captureOpts)

	document.addEventListener('touchstart', onTouchStart, captureOpts)
	document.addEventListener('touchmove', onTouchMove, captureOpts)
	document.addEventListener('touchend', reset, captureOpts)
	document.addEventListener('touchcancel', reset, captureOpts)
}
